/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

export const ORDER_ADVERTISER_MISMATCH_CODES = [
  'validation.warning.creative.advertiser.mismatch.order.advertiser',
  'CREATIVE_ADVERTISER_MISMATCH_ORDER_ADVERTISER'
];

export const FOLIO_ADVERTISER_MISMATCH_CODES = [
  'validation.warning.creative.advertiser.mismatch.folio.advertiser',
  'CREATIVE_ADVERTISER_MISMATCH_FOLIO_ADVERTISER'
];

export const ORDER_CREATIVE_ADVERTISERS_MISMATCH_CODES = [
  'validation.warning.order.advertisers.mismatch.creative.advertisers',
  'ORDER_ADVERTISER_MISMATCH_CREATIVE_ADVERTISERS'
];

export const FOLIO_CREATIVE_ADVERTISERS_MISMATCH_CODES = [
  'validation.warning.folio.advertisers.mismatch.creative.advertisers',
  'FOLIO_ADVERTISER_MISMATCH_CREATIVE_ADVERTISERS'
];

const CREATIVE_ID_PARAM_KEYS = [
  'creatives',
  'creativeIds',
  'creativeId',
  'creativeExternalIds',
  'ids'
];

const EXPECTED_ADVERTISER_OBJECT_PARAM_KEYS = [
  'expectedAdvertiser',
  'orderAdvertiser',
  'folioAdvertiser'
];

const EXPECTED_ADVERTISER_ID_PARAM_KEYS = [
  'expectedAdvertiserId',
  'orderAdvertiserId',
  'folioAdvertiserId'
];

export function warningCode(warning: any): string {
  return warning?.code || warning?.type || '';
}

export function isOrderAdvertiserMismatchWarning(warning: any): boolean {
  return _.includes(ORDER_ADVERTISER_MISMATCH_CODES, warningCode(warning));
}

export function isFolioAdvertiserMismatchWarning(warning: any): boolean {
  return _.includes(FOLIO_ADVERTISER_MISMATCH_CODES, warningCode(warning));
}

export function isCreativeAdvertiserMismatchWarning(warning: any): boolean {
  return isOrderAdvertiserMismatchWarning(warning) || isFolioAdvertiserMismatchWarning(warning);
}

export function findCreativeAdvertiserMismatch(warnings: any): any {
  return _.find(warnings, isCreativeAdvertiserMismatchWarning);
}

export function isOrderCreativeAdvertisersMismatchWarning(warning: any): boolean {
  return _.includes(ORDER_CREATIVE_ADVERTISERS_MISMATCH_CODES, warningCode(warning));
}

export function findOrderCreativeAdvertisersMismatch(warnings: any): any {
  return _.find(warnings, isOrderCreativeAdvertisersMismatchWarning);
}

export function isFolioCreativeAdvertisersMismatchWarning(warning: any): boolean {
  return _.includes(FOLIO_CREATIVE_ADVERTISERS_MISMATCH_CODES, warningCode(warning));
}

export function findFolioCreativeAdvertisersMismatch(warnings: any): any {
  return _.find(warnings, isFolioCreativeAdvertisersMismatchWarning);
}

export function creativeIdsFromWarning(warning: any): string[] {
  const params = warning?.parameters || {};
  const ids: string[] = [];
  _.forEach(CREATIVE_ID_PARAM_KEYS, (key: string) => {
    const value = params[key];
    if (_.isArray(value)) {
      _.forEach(value, (item: any) => {
        if (_.isString(item) && item) {
          ids.push(item);
        } else if (item?.id) {
          ids.push(item.id);
        }
      });
    } else if (_.isString(value) && value) {
      _.forEach(value.split(','), (item: string) => {
        const id = _.trim(item);
        if (id) {
          ids.push(id);
        }
      });
    } else if (value?.id) {
      ids.push(value.id);
    }
  });
  return _.uniq(ids);
}

export function asAdvertiserRef(value: any): {id: string; name?: string} | null {
  if (!value) {
    return null;
  }
  if (_.isString(value)) {
    return {id: value};
  }
  const ref = value as {id?: string; name?: string};
  if (ref.id) {
    return {id: ref.id, name: ref.name};
  }
  return null;
}

export function advertiserRefFromWarning(warning: any): {id: string; name?: string} | null {
  const params = warning?.parameters || {};
  for (let i = 0; i < EXPECTED_ADVERTISER_OBJECT_PARAM_KEYS.length; i++) {
    const value = params[EXPECTED_ADVERTISER_OBJECT_PARAM_KEYS[i]] as {id?: string; name?: string} | undefined;
    if (value && _.isObject(value) && value.id) {
      return {id: value.id, name: value.name};
    }
  }
  for (let i = 0; i < EXPECTED_ADVERTISER_ID_PARAM_KEYS.length; i++) {
    const value = params[EXPECTED_ADVERTISER_ID_PARAM_KEYS[i]];
    if (_.isString(value) && value) {
      return {id: value};
    }
  }
  return null;
}

export function advertiserRefFromRelated(creative: any): {id: string; name?: string} | null {
  return asAdvertiserRef(
    _.get(creative, ['lineItem', 'advertiser'])
    || _.get(creative, ['lineItem', 'order', 'advertiser'])
    || _.get(creative, ['order', 'advertiser'])
    || _.get(creative, ['creativeSet', 'advertiser'])
    || _.get(creative, ['creativeSet', 'folio', 'advertiser'])
    || _.get(creative, ['folio', 'advertiser'])
  );
}
